"use client";
import { useEffect, useMemo, useRef, useState } from "react";

import { useToken } from "../../../../lib/context/auth-context";
import { useGetActiveCart } from "../../../../lib/services/collection-service";
import {
  usePublicOrganisationProductCategories,
  usePublicOrganisationProductFeeds,
} from "../../../../lib/services/product-service";
import {
  buildQueryString,
  extractInfoFromUrl,
  getCategorySlugFromPath,
  getCategoryUrlPath,
  toUrlSlug,
} from "../../../../lib/util";
import { useHistoryManager } from "../../../history/useHistoryManager";
import {
  getActiveCart,
  getCartSource,
  updateCart,
} from "../../../slice/cart-slice";
import {
  getActiveCategory,
  setActiveCategory,
  setCategories,
} from "../../../slice/category-slice";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../../../slice/accessOrg-slice";
import { useAppDispatch, useAppSelector } from "../../../slice/hooks";

const defaultGetShouldShowCover = ({ searchTerm, hasTyped }) =>
  searchTerm === "" && hasTyped === false;

const defaultGetShowHeaderSearch = ({ searchTerm }) => searchTerm !== "";

const defaultGetForceHeaderSearch = () => false;

const defaultGetCoverProps = () => ({});

function BaseStorePage({
  styles,
  StoreBox,
  ProductItem,
  SkeletonCard,
  CoverPage,
  FloatingHelper,
  Pagination,
  CategoryList,
  renderEmptyState,
  getShouldShowCover = defaultGetShouldShowCover,
  getShowHeaderSearch = defaultGetShowHeaderSearch,
  getForceHeaderSearch = defaultGetForceHeaderSearch,
  getCoverProps = defaultGetCoverProps,
  showCategoryList = false,
  categoryListUseFloatingDropdown = false,
  paginationVariant,
  ...props
}) {
  const {
    modal,
    showProduct,
    showCart,
    accessPointId,
    searchTerm,
    setSearchTerm,
    storePath,
    organisation,
    accessPoint,
    categories,
    navLocation,
    urlData,
    urlPath,
    fullUrlPath,
    serverToken,
  } = props;

  const cartSource = useAppSelector(getCartSource);
  const disableStoreProductPrice =
    accessPoint?.settings?.disableStoreFrontPrice || false;

  const { token } = useToken();
  const dispatch = useAppDispatch();
  const requestToken = token ? token : serverToken;
  const searchBarRef = useRef(null);
  const { navigateTo } = useHistoryManager(storePath);

  const [query, setQuery] = useState("");
  const [isShowingFloater, setIsShowingFloater] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= 768,
  );
  const [state, setState] = useState({
    data: [],
    limit: 8,
    total: 0,
    totalPages: 0,
    currentPage: 1,
    page: 1,
    randomSeed: null,
    search: "",
  });

  const cart = useAppSelector(getActiveCart);
  const activeCategory = useAppSelector((state) => getActiveCategory(state));

  const { mutate: mutateActiveCart } = useGetActiveCart({
    onError: () => {},
    onSuccess: async (response) => {
      const cart = response.data.collection;
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
    },
  });

  const {
    data: feeds = [],
    isLoading,
    isFetching,
  } = usePublicOrganisationProductFeeds({
    activeCategory,
    activeSubCategory,
    accessPointId,
    limit: state.limit,
    navLocation,
    organisationId: organisation?.orgId,
    page: state.page,
    query,
    randomSeed: state.randomSeed,
    token: requestToken,
  });

  const { data: loadedCategories = [] } = usePublicOrganisationProductCategories(
    {
      accessPointId,
      organisationId: organisation?.orgId,
      token: requestToken,
    },
  );

  const activeCategoryData = useMemo(() => {
    if (!activeCategory) return null;
    const allCategories =
      Array.isArray(categories) && categories.length
        ? categories
        : loadedCategories;

    return (
      allCategories?.find(
        (category) => category?.productCategoryId === activeCategory,
      ) || null
    );
  }, [activeCategory, categories, loadedCategories]);

  const hasActiveSearch = searchTerm.trim() !== "";
  const viewState = {
    activeCategory,
    activeCategoryData,
    hasActiveSearch,
    hasTyped,
    isMobile,
    searchTerm,
  };
  const shouldShowCover = getShouldShowCover(viewState);
  const shouldShowHeaderSearch = getShowHeaderSearch(viewState);
  const forceHeaderSearch = getForceHeaderSearch(viewState);

  useEffect(() => {
    if (!urlData) return;

    if (urlData.organisation) {
      dispatch(setOrganisation(urlData.organisation));
    }

    if (urlData.accessPointId) {
      dispatch(setAccessPointId(urlData.accessPointId));
    }

    if (urlData.accessPoint) {
      dispatch(setAccessPoint(urlData.accessPoint));
    }

    if (Array.isArray(urlData.accessPoints)) {
      dispatch(setAccessPoints(urlData.accessPoints));
    }
  }, [dispatch, urlData]);

  const storeInState = () => {
    let q = extractInfoFromUrl("q") || "";
    q = q.trim();
    const query = q ? buildQueryString({ q }) : "";
    navigateTo(`${window.location.pathname}${query}`, {
      replace: true,
    });
  };

  const resetPage = (resetCategory = true, persistQuery = true) => {
    if (resetCategory) {
      dispatch(setActiveCategory(""));
      setActiveSubCategory(null);
    }
    if (persistQuery) storeInState();
    setState((prev) => ({
      ...prev,
      data: [],
      total: 0,
      totalPages: 0,
      currentPage: 1,
      page: 1,
      randomSeed: null,
    }));
  };

  const activateLocation = (value) => {
    resetPage();
    dispatch(setAccessPointId(value));
    if (organisation?.orgId && value) {
      mutateActiveCart({
        orgId: organisation?.orgId,
        accessPointId: value,
        cartSource,
      });
    }
  };

  const handleLocationChange = (e) => {
    if (e.target.value === accessPointId) return null;
    activateLocation(e.target.value);
  };

  const openPage = (page) => {
    setState((prev) => ({
      ...prev,
      page,
    }));
  };

  const switchCategory = (id) => {
    resetPage(true, false);
    dispatch(setActiveCategory(id));
    const category = categories?.find(
      (item) => item?.productCategoryId === id,
    );

    if (category?.name) {
      navigateTo(getCategoryUrlPath(storePath, category.name), {}, false);
      return;
    }

    navigateTo(storePath || "home", {}, false);
  };

  const switchSubCategory = ({ categoryId, subCategoryId }) => {
    resetPage(false, false);
    dispatch(setActiveCategory(categoryId));
    setActiveSubCategory(subCategoryId);
  };

  const activateSearch = (value = null) => {
    const rawValue = value !== null ? value : searchTerm || "";
    const trimmed = rawValue.trim();
    if (query.trim() === trimmed) return;

    resetPage(false, false);
    setQuery(trimmed);

    const urlQuery = buildQueryString({ q: trimmed });
    navigateTo(`${window.location.pathname}${urlQuery}`, { replace: true });
  };

  const handleSearchTerm = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    if (feeds && feeds.products) {
      const { products, limit, total, totalPages, currentPage, randomSeed } =
        feeds;
      setState((prev) => ({
        ...prev,
        data: [...(prev.data || []), ...products],
        limit,
        total,
        totalPages,
        currentPage,
        randomSeed: prev.page === 1 ? (randomSeed ?? null) : prev.randomSeed,
      }));
    }
  }, [feeds]);

  useEffect(() => {
    if (loadedCategories && loadedCategories.length > 0) {
      dispatch(setCategories(loadedCategories));
    }
  }, [dispatch, loadedCategories]);

  useEffect(() => {
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : fullUrlPath;
    const categorySlug = getCategorySlugFromPath(currentPath || fullUrlPath);
    if (!categorySlug) {
      if (urlPath === "home" && activeCategory) {
        resetPage(true, false);
      }
      return;
    }
    if (!Array.isArray(categories)) return;

    const category = categories.find(
      (item) => toUrlSlug(item?.name) === categorySlug,
    );

    if (
      category?.productCategoryId &&
      category.productCategoryId !== activeCategory
    ) {
      resetPage(false, false);
      dispatch(setActiveCategory(category.productCategoryId));
    }
  }, [activeCategory, categories, dispatch, fullUrlPath, urlPath]);

  useEffect(() => {
    const q = extractInfoFromUrl("q");
    if (!q) return;
    setSearchTerm(q);
  }, [setSearchTerm]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      setHasTyped(true);
    }

    const delayDebounce = setTimeout(() => {
      activateSearch();
    }, 1000);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  return (
    <StoreBox
      {...props}
      handleLocationChange={handleLocationChange}
      categories={categories}
      switchCategory={switchCategory}
      switchSubCategory={switchSubCategory}
      activeCategory={activeCategory}
      activeSubCategory={activeSubCategory}
      showSearchBar={shouldShowHeaderSearch}
      forceHeaderSearch={forceHeaderSearch}
      headerClassName="no-top-bottom-margin"
      isHomePage={true}
      searchBarRef={searchBarRef}
      hasTyped={hasTyped}
      setHasTyped={setHasTyped}
      showMarketDiv={true}
    >
      <>
        {shouldShowCover && (
          <CoverPage
            organisation={organisation}
            searchTerm={searchTerm}
            activateSearch={activateSearch}
            handleSearchTerm={handleSearchTerm}
            searchBarRef={searchBarRef}
            {...getCoverProps(viewState)}
          />
        )}

        {showCategoryList && CategoryList && (
          <div className={styles.categoryPadCase}>
            <div
              className={styles.categoryPad}
              style={searchTerm ? { marginTop: "3rem" } : null}
            >
              <CategoryList
                categories={categories}
                activeCategory={activeCategory}
                activeSubCategory={activeSubCategory}
                handleCategoryChange={(id) => switchCategory(id)}
                handleSubCategoryChange={switchSubCategory}
                useFloatingDropdown={categoryListUseFloatingDropdown}
              />
            </div>
          </div>
        )}

        <div className={styles.content}>
          <div className={styles.cardWrapper}>
            {!isLoading &&
              state.data.length === 0 &&
              renderEmptyState?.({
                hasActiveSearch,
                setHasTyped,
                setSearchTerm,
                styles,
              })}

            {state.data &&
              state.data.map((item, index) => (
                <ProductItem
                  product={item}
                  key={index}
                  showProduct={showProduct}
                  disableStoreProductPrice={disableStoreProductPrice}
                  accessPoint={accessPoint}
                  showVendor={navLocation === "marketplace"}
                />
              ))}

            {isLoading && state.currentPage === 1 && state.total === 0 && (
              <SkeletonCard />
            )}
          </div>

          <Pagination
            variant={paginationVariant}
            infiniteScroll={true}
            total={state.total}
            limit={state.limit}
            page={state.page}
            onPageClick={openPage}
            enableManualMode={true}
            autoLoadThreshold={5}
            isLoading={isLoading}
            isFetching={isFetching}
            showEndOfList={true}
            endOfListText="🎉 You've reached the end!"
          />

          <FloatingHelper
            modal={modal}
            showCart={showCart}
            cart={cart}
            setIsShowingFloater={setIsShowingFloater}
          />
        </div>
      </>
    </StoreBox>
  );
}

export default BaseStorePage;
